import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
    Container,
    Box,
    Paper,
    Typography,
    Stack,
    Button,
    TextField,
    Grid,
    Divider,
    Alert,
    CircularProgress,
    LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { setPaymentResult, setPaymentError, setPaymentProcessing, resetBooking } from "../redux/slices/bookingSlice";
import { onlinePaymentSession, onlineFinalize, onlineRelease } from "../api/online";
import { loadCseSdk, encryptCardData, validateCardFields } from "../services/cseSdkLoader";
import { shouldReleaseOnExpiry } from "../services/expiryPolicy";
import PaymentBadges from "../components/PaymentBadges";
import { PAYTEN_API } from "../config";

// ESIR broj i fiskalni test režim određuje SERVER (env na functions strani) —
// klijent ih ne šalje; server ionako gazi sve što stigne u body-ju.
const INVOICE_TYPE_NORMAL = "Normal";
const PAYMENT_TYPE_CARD = "Card";

function formatCardNumber(value) {
    const digits = value.replace(/\D/g, "").slice(0, 19);
    return digits.replace(/(.{4})/g, "$1 ").trim();
}

function formatExpiry(value) {
    const digits = value.replace(/\D/g, "").slice(0, 4);
    if (digits.length >= 3) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
}

function ReservationTimer({ secondsLeft, totalSeconds }) {
    const { t } = useTranslation();
    const mins = Math.max(0, Math.floor(secondsLeft / 60));
    const secs = Math.max(0, secondsLeft % 60);
    const progress = totalSeconds > 0 ? Math.max(0, (secondsLeft / totalSeconds) * 100) : 0;
    const isLow = secondsLeft <= 60;

    return (
        <Stack spacing={0.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Icon icon={isLow ? "mdi:clock-alert-outline" : "mdi:shield-lock-outline"} width={18} color={isLow ? "#FF5630" : "#36B37E"} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: isLow ? "error.main" : "text.secondary" }}>
                        {isLow ? t("payment.reservationExpiring") : t("payment.reservationActive")}
                    </Typography>
                </Stack>
                <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: "monospace", color: isLow ? "error.main" : "text.primary" }}>
                    {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                    height: 3,
                    borderRadius: 2,
                    bgcolor: "grey.200",
                    "& .MuiLinearProgress-bar": { bgcolor: isLow ? "error.main" : "success.main" },
                }}
            />
        </Stack>
    );
}

export default function PaymentPage({ embedded = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { bookingResult, paymentProcessing, paymentError } = useSelector((state) => state.booking);
    const { selectedTrip, passengers: passengerCount } = useSelector((state) => state.search);

    // Test kartica se pretpopunjava samo u test okruženjima (lokalni dev ili
    // FISCAL_TEST_MODE=true, npr. staging); na produkciji su polja prazna.
    const PREFILL_TEST_CARD =
        process.env.NODE_ENV === "development" ||
        process.env.REACT_APP_FISCAL_TEST_MODE === "true";
    const [cardNumber, setCardNumber] = useState(PREFILL_TEST_CARD ? "5337 6465 4096 8594" : "");
    const [expiry, setExpiry] = useState(PREFILL_TEST_CARD ? "12/40" : "");
    const [cvv, setCvv] = useState(PREFILL_TEST_CARD ? "000" : "");
    const [cardHolder, setCardHolder] = useState(PREFILL_TEST_CARD ? "TEST TEST" : "");
    const [initError, setInitError] = useState(null);
    const [initializing, setInitializing] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);
    const [processingStep, setProcessingStep] = useState("");
    const [secondsLeft, setSecondsLeft] = useState(0);
    const expiredHandledRef = useRef(false);

    const merchantPaymentId = bookingResult?.merchantPaymentId;
    const ticketIds = useMemo(() => bookingResult?.ticketIds || [], [bookingResult]);
    const totalPrice = bookingResult?.amount || 0;
    const reservedAt = bookingResult?.reservedAt;
    const ttlSeconds = bookingResult?.ttlSeconds || 300;
    const expiresAt = reservedAt ? reservedAt + ttlSeconds * 1000 : null;

    // Best-effort release ako korisnik napusti stranicu pre uspele fiskalizacije.
    // React StrictMode u dev-u izvršava efekte dvaput (mount → cleanup → mount),
    // pa moramo razlikovati StrictMode dupliranje od stvarnog unmount-a:
    // odložimo release sa 250ms delay, a sledeći mount poništi pending timer.
    const finalizedRef = useRef(false);
    const merchantIdRef = useRef(merchantPaymentId);
    const ticketIdsRef = useRef(ticketIds);
    // Ref ogledalo paymentProcessing-a — tajmer isteka čita kroz ref da
    // interval closure uvek vidi sveže stanje naplate.
    const paymentProcessingRef = useRef(paymentProcessing);
    useEffect(() => { merchantIdRef.current = merchantPaymentId; }, [merchantPaymentId]);
    useEffect(() => { ticketIdsRef.current = ticketIds; }, [ticketIds]);
    useEffect(() => { paymentProcessingRef.current = paymentProcessing; }, [paymentProcessing]);
    useEffect(() => {
        let pendingReleaseTimer = null;
        let unmounted = false;
        // Ako je prethodni unmount zakazao release a mi smo se vratili — otkaži.
        if (typeof window !== "undefined" && window.__pendingReleaseTimer) {
            clearTimeout(window.__pendingReleaseTimer);
            window.__pendingReleaseTimer = null;
        }
        return () => {
            unmounted = true;
            pendingReleaseTimer = setTimeout(() => {
                if (!unmounted) return;
                if (finalizedRef.current || expiredHandledRef.current) return;
                const id = merchantIdRef.current;
                const ids = ticketIdsRef.current;
                if (!id || !ids || ids.length === 0) return;
                onlineRelease({ merchantPaymentId: id, ticketIds: ids }).catch(() => {});
                window.__pendingReleaseTimer = null;
            }, 250);
            window.__pendingReleaseTimer = pendingReleaseTimer;
        };
    }, []);

    const handleExpiry = useCallback(async () => {
        // Istek TOKOM naplate NE otpušta karte — čeka se ishod /finalize
        // (uspeh vodi na potvrdu; neuspeh → sledeći otkucaj tajmera otpušta).
        if (!shouldReleaseOnExpiry({
            alreadyHandled: expiredHandledRef.current,
            paymentProcessing: paymentProcessingRef.current,
        })) return;
        expiredHandledRef.current = true;
        try {
            if (merchantPaymentId && ticketIds.length > 0) {
                await onlineRelease({ merchantPaymentId, ticketIds });
            }
        } catch (e) {
            // best-effort; backend će ionako pustiti karte po TTL-u
        } finally {
            dispatch(resetBooking());
            enqueueSnackbar(t("payment.expiredReleased"), { variant: "warning" });
            // U mobilnom WebView-u nativna app rukuje istekom (svoj „expired" ekran),
            // pa korisnika ne šaljemo na web početnu.
            if (!embedded) navigate("/");
        }
    }, [merchantPaymentId, ticketIds, dispatch, enqueueSnackbar, navigate, t, embedded]);

    useEffect(() => {
        // U mobilnom WebView-u nativna aplikacija ima svoj tajmer i „istekla
        // rezervacija" ekran — web tajmer se ne pokreće da ne bi dolazilo do
        // dvostrukog odbrojavanja ni web redirecta na istek.
        if (embedded || !expiresAt) return;
        // 10s safety bufer — UI istekne 10s pre nego što backend pusti karte,
        // da bi se izbegao race ako klijentski sat odstupa.
        const safeExpiresAt = expiresAt - 10_000;
        const tick = () => {
            const left = Math.max(0, Math.ceil((safeExpiresAt - Date.now()) / 1000));
            setSecondsLeft(left);
            if (left <= 0) {
                handleExpiry();
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [expiresAt, handleExpiry, embedded]);

    useEffect(() => {
        if (!merchantPaymentId) return;

        let cancelled = false;
        setInitializing(true);
        setInitError(null);

        (async () => {
            try {
                const returnUrl = `${PAYTEN_API}/3d-return?spaOrigin=${encodeURIComponent(window.location.origin)}`;
                const session = await onlinePaymentSession({ merchantPaymentId, returnUrl });
                if (cancelled) return;
                await loadCseSdk(session.sdkUrl, session.sessionToken);
                if (cancelled) return;
                setSessionReady(true);
            } catch (err) {
                if (cancelled) return;
                setInitError(err?.message || "Nije moguće inicijalizovati plaćanje");
            } finally {
                if (!cancelled) setInitializing(false);
            }
        })();

        return () => { cancelled = true; };
    }, [merchantPaymentId]);

    const cleanPan = cardNumber.replace(/\s/g, "");

    const isFormValid = () => {
        return (
            cleanPan.length >= 13 &&
            cleanPan.length <= 19 &&
            expiry.length === 5 &&
            cvv.length >= 3 &&
            cardHolder.length >= 3
        );
    };

    const handlePay = useCallback(async () => {
        if (!isFormValid() || !sessionReady || !merchantPaymentId) return;

        dispatch(setPaymentProcessing(true));
        dispatch(setPaymentError(null));

        try {
            const [mm, yy] = expiry.split("/");
            const expiryYear4 = yy.length === 2 ? `20${yy}` : yy;
            const cardData = {
                pan: cleanPan,
                cardHolderName: cardHolder,
                expiryMonth: mm,
                expiryYear: expiryYear4,
                cvv,
            };

            const validation = validateCardFields(cardData);
            if (!validation.valid) {
                throw new Error(`Neispravni podaci kartice: ${validation.errors.join(", ")}`);
            }

            setProcessingStep(t("payment.encryptingCard"));
            const encryptedData = await encryptCardData(cardData);

            setProcessingStep(t("payment.chargingFiscalizing"));
            const result = await onlineFinalize({
                merchantPaymentId,
                payten: { method: "CSE", encryptedData },
                reservation: {
                    bookingId: null,
                    ticketIds,
                    services: [],
                    payment: [{ paymentType: PAYMENT_TYPE_CARD, amount: totalPrice }],
                    invoiceType: INVOICE_TYPE_NORMAL,
                    cashier: bookingResult?.cashier || "bussoft-client",
                    email: bookingResult?.customer?.email || null,
                    emailSubject: "BusGo - Vaša karta",
                    buyerId: null,
                    buyerCostCenterId: null,
                    isExternalSale: false,
                    isOnlineSale: true,
                },
            });

            finalizedRef.current = true;
            dispatch(setPaymentResult(result));
            navigate("/potvrda");
        } catch (err) {
            const detail = err?.fiscalizationError || err?.voidError || "";
            const msg =
                (err?.message || err?.error || "Greška pri plaćanju") +
                (detail ? ` (${detail})` : "");
            dispatch(setPaymentError(msg));
        } finally {
            dispatch(setPaymentProcessing(false));
            setProcessingStep("");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cleanPan, expiry, cvv, cardHolder, sessionReady, merchantPaymentId, ticketIds, totalPrice]);

    if (!bookingResult || !merchantPaymentId) {
        return (
            <Box sx={{ minHeight: "calc(100vh - 64px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Stack alignItems="center" spacing={2}>
                    <Typography>{t("payment.noReservation")}</Typography>
                    {!embedded && (
                        <Button variant="contained" onClick={() => navigate("/")}>{t("payment.backToHome")}</Button>
                    )}
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="md">
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    {!embedded && (
                        <Button startIcon={<Icon icon="mdi:arrow-left" />} onClick={() => navigate("/rezervacija")} sx={{ fontWeight: 600 }}>
                            {t("common.back")}
                        </Button>
                    )}
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                        {t("payment.title")}
                    </Typography>
                </Stack>

                {initError && (
                    <Alert severity="error" sx={{ mb: 3 }}>{initError}</Alert>
                )}

                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Paper sx={{ p: 3, borderRadius: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                <Icon icon="mdi:credit-card-outline" width={24} color="#1a1a2e" />
                                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
                                    {PREFILL_TEST_CARD ? t("payment.cardDetailsTest") : t("payment.cardDetails")}
                                </Typography>
                            </Stack>

                            {initializing && !sessionReady && (
                                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                                    <CircularProgress size={18} />
                                    <Typography variant="body2" color="text.secondary">
                                        {t("payment.secureSession")}
                                    </Typography>
                                </Stack>
                            )}

                            <Stack spacing={2.5} component="form" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                                <TextField
                                    fullWidth
                                    label={t("payment.cardNumber")}
                                    placeholder="0000 0000 0000 0000"
                                    value={cardNumber}
                                    onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                                    disabled={paymentProcessing || !sessionReady}
                                    inputProps={{ maxLength: 23, autoComplete: "off", inputMode: "numeric" }}
                                />

                                <TextField
                                    fullWidth
                                    label={t("payment.cardHolder")}
                                    placeholder="IME PREZIME"
                                    value={cardHolder}
                                    onChange={(e) => setCardHolder(e.target.value.toUpperCase())}
                                    disabled={paymentProcessing || !sessionReady}
                                    inputProps={{ autoComplete: "off" }}
                                />

                                <Stack direction="row" spacing={2}>
                                    <TextField
                                        fullWidth
                                        label={t("payment.expiryDate")}
                                        placeholder="MM/GG"
                                        value={expiry}
                                        onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                                        disabled={paymentProcessing || !sessionReady}
                                        inputProps={{ maxLength: 5, autoComplete: "off", inputMode: "numeric" }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="CVV"
                                        placeholder="000"
                                        type="password"
                                        value={cvv}
                                        onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                                        disabled={paymentProcessing || !sessionReady}
                                        inputProps={{ maxLength: 4, autoComplete: "off", inputMode: "numeric" }}
                                    />
                                </Stack>
                            </Stack>

                            {paymentProcessing && (
                                <Paper elevation={0} sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "primary.lighter" }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <CircularProgress size={20} sx={{ color: "primary.main" }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {processingStep || t("payment.processing")}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            )}

                            {paymentError && (
                                <Alert severity="error" sx={{ mt: 2 }}>{paymentError}</Alert>
                            )}

                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                onClick={handlePay}
                                disabled={paymentProcessing || !sessionReady || !isFormValid()}
                                startIcon={paymentProcessing ? <CircularProgress size={20} color="inherit" /> : <Icon icon="mdi:lock-check" />}
                                sx={{ mt: 3, py: 1.5, fontSize: "1.05rem", fontWeight: 700, borderRadius: 2 }}
                            >
                                {paymentProcessing ? t("payment.processing") : `${t("payment.payNow")} ${totalPrice.toFixed(2)} RSD`}
                            </Button>

                            <Divider sx={{ my: 2.5 }} />
                            <PaymentBadges />
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Stack spacing={2} sx={{ position: "sticky", top: 80 }}>
                            {!embedded && expiresAt && (
                                <Paper sx={{ p: 2, borderRadius: 2 }}>
                                    <ReservationTimer secondsLeft={secondsLeft} totalSeconds={ttlSeconds} />
                                </Paper>
                            )}
                            <Paper sx={{ p: 3, borderRadius: 2 }}>
                                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mb: 2 }}>
                                    {t("payment.summary")}
                                </Typography>
                                <Stack spacing={1.5}>
                                    {selectedTrip && (
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography variant="body2" color="text.secondary">{t("payment.relation")}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {selectedTrip.departureStationName || selectedTrip.fromStation} {" → "}
                                                {selectedTrip.arrivalStationName || selectedTrip.toStation}
                                            </Typography>
                                        </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">{t("payment.passengers")}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{passengerCount}</Typography>
                                    </Stack>
                                    {bookingResult?.selectedSeats?.length > 0 && (
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="body2" color="text.secondary">{t("payment.seats")}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {bookingResult.selectedSeats
                                                    .map((s) => s?.seatNumber)
                                                    .filter(Boolean)
                                                    .join(", ")}
                                            </Typography>
                                        </Stack>
                                    )}
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">{t("payment.ticketIds")}</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {ticketIds.join(", ")}
                                        </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">{t("payment.order")}</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                                            {merchantPaymentId.slice(0, 8)}...
                                        </Typography>
                                    </Stack>
                                    <Divider />
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 700 }}>{t("payment.total")}</Typography>
                                        <Typography variant="h5" component="span" sx={{ fontWeight: 800, color: "primary.main" }}>
                                            {totalPrice.toFixed(2)} RSD
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>

                            <Paper sx={{ p: 2, borderRadius: 2, bgcolor: "success.lighter" }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Icon icon="mdi:ticket-confirmation" width={20} color="#1B806A" />
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: "success.darker" }}>
                                        {t("payment.ticketReserved")}
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}
